import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useProgress } from '../hooks/useProgress';
import { Nav } from '../components/Nav';
import { ProgressBar } from '../components/ProgressBar';
import { DayCard } from '../components/DayCard';
import { BookingModal } from '../components/BookingModal';
import { RescheduleModal } from '../components/RescheduleModal';
import { SupportModal } from '../components/SupportModal';
import { getBooking } from '../utils/api';
import { DAYS, C } from '../utils/constants';
import { formatClockTime } from '../utils/formatTime';

const useCountdown = (targetDate) => {
  const calc = useCallback(() => {
    if (!targetDate) return null;
    const diff = new Date(targetDate) - new Date();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, past: true };
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff % 86400000) / 3600000),
      minutes: Math.floor((diff % 3600000) / 60000),
      seconds: Math.floor((diff % 60000) / 1000),
      past: false,
    };
  }, [targetDate]);

  const [countdown, setCountdown] = useState(calc);
  useEffect(() => {
    if (!targetDate) return;
    setCountdown(calc());
    const id = setInterval(() => setCountdown(calc()), 1000);
    return () => clearInterval(id);
  }, [targetDate, calc]);
  return countdown;
};

// (Removed: SESSION_UNLOCK_WINDOW_MS — the 48h time-gate on both
// "prepare for your session" surfaces is gone. The banner button was
// deleted entirely; the Call Day card button is now ungated on
// booking.callBooked.)

// A user cannot book a BMS session until 7 days after their first login
// (fallback: signup / created_at). This gives them time with the course
// before the 1:1. This is UX gating only — the real enforcement lives in
// the RY backend (/api/user/can-book-bms) and the Booking Hub
// (/api/public/bookings server-side backstop).
const BMS_BOOK_LOCK_MS = 7 * 24 * 60 * 60 * 1000; // 7d

const ENCOURAGEMENTS = [
  "Let's get the ball rolling!",
  "1 down, 5 to go — You've got this!",
  "Keep going! Every step you take is loosening old patterns and strengthening your perceived self-value!",
  "I know this can feel uncomfortable… but you're doing amazingly! I believe in you!",
  "You've got this! Know that it feeling hard is part of the process too!",
  "You're closer than you think — Don't stop now!",
  "Congratulations — You made it! See you at our Breakthrough Mapping Session!",
];

export const Dashboard = () => {
  const { user, logout } = useAuth();
  const { getDayProgress, isDayUnlocked, isDayComplete, completedDays } = useProgress();
  const navigate = useNavigate();
  const [showBooking, setShowBooking] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [booking, setBooking] = useState(null);

  const countdown = useCountdown(booking?.sessionDate);

  // Live Zoom window: from 10 min before the session start to 2 hr after.
  // Derived each render; the useCountdown 1s tick re-renders the component,
  // so this re-evaluates without adding a second timer.
  const zoomWindowOpen =
    !!booking?.sessionDate &&
    !!booking?.zoomLink &&
    Date.now() >= new Date(booking.sessionDate).getTime() - 10 * 60 * 1000 &&
    Date.now() <= new Date(booking.sessionDate).getTime() + 2 * 60 * 60 * 1000;

  // (Removed: `prepareUnlocked` derivation. The banner-button gate it fed
  // has been deleted; the Call Day card button is now ungated on
  // booking.callBooked. No time-based unlock is computed at this level.)

  // BMS booking lock — 7 days from first_login (fallback created_at).
  // Both timestamps now come back from /api/user/me (firstLogin, createdAt).
  // If neither is present (very early account state, before useAuth resolves)
  // we default to LOCKED so we never accidentally show the book button to a
  // user we can't yet evaluate.
  const bmsAnchorIso = user?.firstLogin || user?.createdAt || null;
  const bmsUnlockMs = bmsAnchorIso
    ? new Date(bmsAnchorIso).getTime() + BMS_BOOK_LOCK_MS
    : null;
  const bmsBookingUnlocked = bmsUnlockMs !== null && Date.now() >= bmsUnlockMs;
  const bmsUnlockDateStr = bmsUnlockMs
    ? new Date(bmsUnlockMs).toLocaleDateString('en-GB', {
        weekday: 'long', day: 'numeric', month: 'long',
      })
    : null;

  const fetchBooking = useCallback(async () => {
    try {
      const res = await getBooking();
      setBooking(res.data);
    } catch (e) { /* not critical */ }
  }, []);

  useEffect(() => { fetchBooking(); }, [fetchBooking]);

  // Poll for booking if not yet confirmed (catches Booking Hub webhook delay)
  useEffect(() => {
    if (booking?.callBooked) return;
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      const res = await getBooking().catch(() => null);
      if (res?.data?.callBooked) { setBooking(res.data); clearInterval(interval); }
      if (attempts >= 10) clearInterval(interval);
    }, 4000);
    return () => clearInterval(interval);
  }, [booking?.callBooked]);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleModalClose = () => {
    setShowBooking(false);
    // Re-check booking status after modal closes in case Booking Hub webhook already fired
    setTimeout(fetchBooking, 1500);
  };

  const firstName = user?.firstName || user?.email?.split('@')[0] || 'there';

  // Current day = first incomplete unlocked day, or last
  const currentDay = (() => {
    for (let i = 1; i <= 6; i++) {
      if (!isDayComplete(i)) return i;
    }
    return 6;
  })();

  const encouragement = ENCOURAGEMENTS[Math.min(completedDays, 6)];

  const handleDayClick = (dayNum) => {
    navigate(`/day/${dayNum}/video`);
  };

  const handleAdmin = () => navigate('/admin');

  return (
    <div style={{ minHeight: '100vh', background: C.cream }}>
      <Nav user={user} onAdmin={handleAdmin} />
      <ProgressBar completedDays={completedDays} />

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '32px 16px 64px' }}>
        {/* Completed state banner */}
        {completedDays === 6 && (
          <div style={{
            background: `linear-gradient(135deg, #1a0800 0%, ${C.brown} 100%)`,
            borderRadius: 16,
            padding: '24px 28px',
            marginBottom: 24,
            textAlign: 'center',
            boxShadow: '0 6px 32px rgba(61,31,2,0.18)',
          }}>
            <div style={{ fontSize: '2rem', marginBottom: 6 }}>🏆</div>
            <h2 style={{
              fontFamily: "'Forum', serif",
              color: C.gold,
              fontSize: '1.3rem',
              letterSpacing: '0.04em',
              marginBottom: 8,
            }}>
              Program Complete — Well Done, {firstName}!
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.88rem', lineHeight: 1.6, marginBottom: 16 }}>
              You can still revisit and rewatch any day below whenever you like.
            </p>
            <button
              className="btn-primary"
              onClick={() => navigate('/completion')}
              style={{ background: C.gold, color: C.darkBrown, fontSize: '0.9rem', padding: '10px 24px' }}
            >
              Revisit Your Celebration →
            </button>
          </div>
        )}

        {/* Welcome heading */}
        <div className="animate-in" style={{ marginBottom: 8 }}>
          <h1 style={{
            fontFamily: "'Forum', serif",
            color: C.brown,
            fontSize: 'clamp(1.5rem, 4vw, 2rem)',
            letterSpacing: '0.02em',
            marginBottom: 6,
          }}>
            Welcome, {firstName} 👋
          </h1>
          <p style={{
            color: C.terracotta,
            fontSize: '0.95rem',
          }}>
            {completedDays === 6
              ? "You've completed all 6 days — your journey continues. 🌟"
              : `You're on Day ${currentDay} — ${encouragement}`}
          </p>
        </div>

        {/* Booking banner */}
        {booking?.callBooked ? (
          /* ── BOOKED STATE ── */
          <div style={{
            background: `linear-gradient(135deg, ${C.darkBrown} 0%, ${C.brown} 100%)`,
            borderRadius: 14,
            padding: '14px 18px',
            margin: '28px 0',
            boxShadow: '0 4px 20px rgba(61,31,2,0.2)',
            textAlign: 'center',
          }}>
            <div style={{
              fontFamily: "'Forum', serif",
              fontSize: '1.3rem',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: C.gold,
              marginBottom: 8,
            }}>
              ✓ Your Session Is Booked
            </div>
            <div style={{
              fontFamily: "'Forum', serif",
              color: '#fff',
              fontSize: '1.2rem',
              marginBottom: 6,
            }}>
              Breakthrough Mapping Session · 1:1 with Ben
            </div>

            {countdown && !countdown.past && booking.sessionDate ? (
              <>
                <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', marginBottom: 20 }}>
                  Your session is in:
                </p>
                <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
                  {[
                    { val: countdown.days, label: 'Days' },
                    { val: countdown.hours, label: 'Hours' },
                    { val: countdown.minutes, label: 'Mins' },
                    { val: countdown.seconds, label: 'Secs' },
                  ].map(({ val, label }) => (
                    <div key={label} style={{
                      background: 'rgba(255,255,255,0.12)',
                      borderRadius: 10,
                      padding: '12px 16px',
                      minWidth: 64,
                    }}>
                      <div style={{
                        fontFamily: "'Forum', serif",
                        fontSize: '2rem',
                        color: C.gold,
                        lineHeight: 1,
                      }}>
                        {String(val).padStart(2, '0')}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)', marginTop: 4 }}>
                        {label}
                      </div>
                    </div>
                  ))}
                </div>
                <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.8rem', marginTop: 16 }}>
                  {new Date(booking.sessionDate).toLocaleDateString('en-GB', {
                    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
                    hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
                  }).replace(/\b\d{1,2}:\d{2}\b/, formatClockTime(new Date(booking.sessionDate)))}
                </p>
              </>
            ) : !countdown?.past ? (
              <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.95rem', marginTop: 8 }}>
                Your session is confirmed. Check your email for the Zoom link. 🎯
              </p>
            ) : null}

            {zoomWindowOpen && (
              <a
                href={booking.zoomLink}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  marginTop: 16,
                  fontFamily: "'Forum', serif",
                  background: 'linear-gradient(135deg, #2D8CFF, #1a6fd4)',
                  color: '#fff',
                  padding: '14px 32px',
                  borderRadius: 8,
                  fontSize: '1rem',
                  fontWeight: 700,
                  textDecoration: 'none',
                  boxShadow: '0 4px 20px rgba(45,140,255,0.5)',
                }}
              >
                Join on Zoom →
              </a>
            )}
            {/* (Removed: "Prepare for Your Session →" button — the route
                to /call-day is surfaced from the bottom Call Day card
                instead, the moment a booking exists.) */}

            {/* Quiet reschedule link — sits in the booked-session details so it
                is visible the WHOLE time the client is booked (NOT tied to the
                live-window Join button). Only with an appointment id and before
                the session has passed. Opens the native in-portal RescheduleModal
                (was a link to the booking-hub manage page). */}
            {booking?.appointmentId && !countdown?.past && (
              <div style={{ marginTop: 16 }}>
                <button
                  type="button"
                  onClick={() => setShowReschedule(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    color: 'rgba(255,255,255,0.6)',
                    fontSize: '0.8rem',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  Need to reschedule?
                </button>
              </div>
            )}
          </div>
        ) : (
          /* ── UNBOOKED STATE ── */
          <div
            className="booking-banner"
            style={{
              background: `linear-gradient(135deg, ${C.darkBrown} 0%, ${C.brown} 100%)`,
              borderRadius: 14,
              padding: '20px 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
              margin: '28px 0',
              boxShadow: '0 4px 20px rgba(61,31,2,0.2)',
            }}
          >
            <div>
              <div style={{
                fontFamily: "'Forum', serif",
                fontSize: '0.85rem',
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: C.gold,
                marginBottom: 6,
              }}>
                Included in Your Program
              </div>
              <div style={{
                fontFamily: "'Forum', serif",
                color: '#fff',
                fontSize: '1.35rem',
                letterSpacing: '0.02em',
              }}>
                Breakthrough Mapping Session
              </div>
              <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.85rem', marginTop: 2 }}>
                90 min · 1:1 with Ben · Via Zoom
              </div>
            </div>
            <button
              onClick={() => setShowBooking(true)}
              style={{
                fontFamily: "'Forum', serif",
                background: `linear-gradient(135deg, ${C.amber}, ${C.gold})`,
                border: 'none',
                color: C.darkBrown,
                padding: '12px 22px',
                borderRadius: 8,
                fontSize: '0.9rem',
                fontWeight: 700,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                boxShadow: '0 3px 12px rgba(245,166,35,0.4)',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 5px 18px rgba(245,166,35,0.5)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 3px 12px rgba(245,166,35,0.4)';
              }}
            >
              Book Now →
            </button>
          </div>
        )}

        {/* Your 7 Days */}
        <div style={{ marginBottom: 40 }}>
          <h2 style={{
            fontFamily: "'Forum', serif",
            fontSize: '0.72rem',
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: C.terracotta,
            marginBottom: 16,
          }}>
            Your 6 Days
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {DAYS.map(day => (
              <DayCard
                key={day.num}
                day={day}
                progressData={getDayProgress(day.num)}
                isUnlocked={isDayUnlocked(day.num)}
                onClick={() => handleDayClick(day.num)}
              />
            ))}
          </div>
        </div>

        {/* ── Call Day section ── */}
        {(() => {
          const GOLD = '#F5A623';
          const DARK_GOLD = '#E8830A';
          // No time-based gating any more — the card is fully "unlocked"
          // the moment booking.callBooked is true. The 48h `unlocked` /
          // `locked` flags, the unlockDateStr / unlockTimeStr helpers and
          // the SESSION_UNLOCK_WINDOW_MS constant have all been removed.
          // The not-booked path's 7-day BMS lock (bmsBookingUnlocked +
          // bmsUnlockDateStr) is preserved — that's a different rule.
          const isBooked = !!booking?.callBooked;

          return (
            <div style={{ marginBottom: 40 }}>
              <h2 style={{
                fontFamily: "'Forum', serif",
                fontSize: '1.4rem',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: GOLD,
                marginBottom: 16,
              }}>
                Your Call Day
              </h2>

              <div
                onClick={isBooked ? () => navigate('/call-day') : undefined}
                style={{
                  background: `linear-gradient(135deg, ${C.darkBrown} 0%, ${C.brown} 100%)`,
                  borderRadius: 14,
                  padding: '20px 24px',
                  border: `2px solid ${isBooked ? GOLD : 'rgba(245,166,35,0.25)'}`,
                  boxShadow: isBooked ? `0 4px 24px rgba(245,166,35,0.2)` : '0 2px 12px rgba(0,0,0,0.15)',
                  cursor: isBooked ? 'pointer' : 'default',
                  transition: 'box-shadow 0.2s ease',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                onMouseEnter={e => { if (isBooked) e.currentTarget.style.boxShadow = '0 6px 32px rgba(245,166,35,0.35)'; }}
                onMouseLeave={e => { if (isBooked) e.currentTarget.style.boxShadow = '0 4px 24px rgba(245,166,35,0.2)'; }}
              >
                {/* Top label */}
                <div style={{
                  fontFamily: "'Forum', serif",
                  fontSize: '0.68rem',
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: GOLD,
                  marginBottom: 6,
                  opacity: isBooked ? 1 : 0.65,
                }}>
                  {isBooked ? '✓ Unlocked' : 'Your 1:1 Session'}
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 16,
                  flexWrap: 'wrap',
                }}>
                  <div>
                    <div style={{
                      fontFamily: "'Forum', serif",
                      color: '#fff',
                      fontSize: '1.2rem',
                      letterSpacing: '0.02em',
                      marginBottom: 4,
                      opacity: isBooked ? 1 : 0.75,
                    }}>
                      {isBooked && booking?.zoomLink
                        ? 'Breakthrough Mapping Session — Zoom Link Inside'
                        : 'Breakthrough Mapping Session'}
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.82rem' }}>
                      {isBooked
                        ? 'Prep video · Pre-call form — everything you need for your session'
                        : !bmsBookingUnlocked && bmsUnlockDateStr
                        ? `Booking opens after you've had a few days with the course. Booking unlocks on ${bmsUnlockDateStr}.`
                        : 'Book your session to unlock this section'}
                    </div>
                  </div>

                  {/* "Prepare for Your Session →" CTA — ungated. Visible
                      the moment a booking exists, regardless of how far
                      out the session is. */}
                  {isBooked && (
                    <div style={{
                      fontFamily: "'Forum', serif",
                      background: `linear-gradient(135deg, ${DARK_GOLD}, ${GOLD})`,
                      color: '#3D1F02',
                      padding: '10px 20px',
                      borderRadius: 8,
                      fontSize: '0.9rem',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      boxShadow: '0 3px 12px rgba(245,166,35,0.4)',
                    }}>
                      Prepare Now →
                    </div>
                  )}

                  {/* Not-yet-booked: surface a CTA that opens the same
                      BookingModal the dashboard already uses. Same source
                      of truth (booking?.callBooked) — no new state. The
                      button is additionally gated on the 7-day-from-
                      first-login lock; before that the right-hand slot
                      shows a 🔒. */}
                  {!isBooked && !bmsBookingUnlocked && (
                    <div style={{
                      fontSize: '1.5rem',
                      opacity: 0.3,
                    }}>
                      🔒
                    </div>
                  )}

                  {!isBooked && bmsBookingUnlocked && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowBooking(true); }}
                      style={{
                        fontFamily: "'Forum', serif",
                        background: `linear-gradient(135deg, ${DARK_GOLD}, ${GOLD})`,
                        border: 'none',
                        color: '#3D1F02',
                        padding: '10px 20px',
                        borderRadius: 8,
                        fontSize: '0.9rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        boxShadow: '0 3px 12px rgba(245,166,35,0.4)',
                        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 5px 18px rgba(245,166,35,0.5)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 3px 12px rgba(245,166,35,0.4)';
                      }}
                    >
                      Book Your Breakthrough Mapping Session →
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Community section */}
        <div style={{
          background: '#fff',
          borderRadius: 14,
          padding: '24px 28px',
          border: `1.5px solid ${C.border}`,
          textAlign: 'center',
        }}>
          <div style={{
            fontFamily: "'Forum', serif",
            color: C.brown,
            fontSize: '1.1rem',
            marginBottom: 8,
          }}>
            Join Your Community
          </div>
          <p style={{ color: C.terracotta, fontSize: '0.85rem', marginBottom: 18 }}>
            Connect with others doing this work. Share wins, get support, grow together.
          </p>
          <a
            href="https://www.facebook.com/groups/reconditionyourself"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            style={{
              display: 'inline-block',
              background: '#1877F2',
              padding: '12px 28px',
            }}
          >
            👥 Join Facebook Group
          </a>

          <>
              <div style={{
                borderTop: `1px solid ${C.border}`,
                margin: '20px -28px 0',
                padding: '20px 28px 0',
              }}>
                <div style={{
                  fontFamily: "'Forum', serif",
                  color: C.brown,
                  fontSize: '1.1rem',
                  marginBottom: 8,
                }}>
                  Your Journal
                </div>
                <p style={{ color: C.terracotta, fontSize: '0.85rem', marginBottom: 16 }}>
                  Check out what you've been writing and reflecting on across your journey.
                </p>
                <button
                  onClick={() => navigate('/my-journal')}
                  className="btn-primary"
                  style={{ background: C.gold, color: C.darkBrown, padding: '12px 28px' }}
                >
                  📝 View My Journal
                </button>
              </div>
            </>
        </div>
      </div>

      {/* Footer */}
      <div style={{ textAlign: 'center', padding: '8px 16px 32px', marginTop: -8 }}>
        <button
          onClick={() => setShowSupport(true)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: C.terracotta, fontSize: '0.78rem', opacity: 0.7,
            fontFamily: "'Nunito', sans-serif",
            textDecoration: 'underline',
          }}
        >
          Questions or technical issues? Get in touch
        </button>
      </div>

      {showBooking && <BookingModal onClose={handleModalClose} daysCompleted={completedDays} />}
      {showReschedule && booking?.appointmentId && (
        <RescheduleModal
          booking={booking}
          onClose={() => setShowReschedule(false)}
          onRescheduled={fetchBooking}
        />
      )}
      {showSupport && <SupportModal onClose={() => setShowSupport(false)} />}
    </div>
  );
};
